import { memo, useCallback, useEffect, useMemo, useState } from "react";

import Navbar from "../navbar/Navbar";
import "./Home.css";
import Collections from "./collections/Collections";
import Features from "./Feature/Features";
import Shapingd from "./shaping/ Shapingd";
import Footer from "../Footer/Footer";
import ExploreCollection from "./exploreCollection/ExploreCollection";
import HeroSection from "./herosection/HeroSection";
import NewArrivalSection from "./herosection/NewArrivalSection";

const API_ROOT = "https://admin.monsinidress.com";
const NAVBAR_URL = `${API_ROOT}/api/navbar`;
const PRODUCTS_URL = `${API_ROOT}/api/products`;

const DEFAULT_YEAR = 2026;
const HOME_PRODUCTS_LIMIT = 50;
const REQUEST_TIMEOUT_MS = 30000;
const CACHE_KEY = "risky-home-products-v2";
const CACHE_TTL_MS = 5 * 60 * 1000;

function getNumericYears(value) {
	if (!Array.isArray(value)) {
		return [];
	}

	return value
		.map((year) => Number(year))
		.filter((year) => Number.isFinite(year));
}

function getLatestYear(years) {
	const numericYears = getNumericYears(years);
	return numericYears.length ? Math.max(...numericYears) : DEFAULT_YEAR;
}

function readHomeCache() {
	try {
		const raw = window.sessionStorage.getItem(CACHE_KEY);
		if (!raw) {
			return null;
		}

		const parsed = JSON.parse(raw);
		const isFresh =
			Number.isFinite(Number(parsed?.savedAt)) &&
			Date.now() - Number(parsed.savedAt) < CACHE_TTL_MS;

		if (!isFresh || !Array.isArray(parsed?.products)) {
			window.sessionStorage.removeItem(CACHE_KEY);
			return null;
		}

		return {
			products: parsed.products,
			years:
				Array.isArray(parsed?.years) && parsed.years.length
					? parsed.years
					: [DEFAULT_YEAR],
		};
	} catch (_) {
		return null;
	}
}

function saveHomeCache(products, years) {
	try {
		window.sessionStorage.setItem(
			CACHE_KEY,
			JSON.stringify({
				products,
				years,
				savedAt: Date.now(),
			})
		);
	} catch (_) {
		// Storage can be unavailable or full. The page still works without cache.
	}
}

async function fetchJson(url, options = {}, signal) {
	const response = await fetch(url, {
		...options,
		signal,
	});

	if (!response.ok) {
		throw new Error(`Request failed with status ${response.status}`);
	}

	return response.json();
}

function Home() {
	const initialCache = useMemo(() => readHomeCache(), []);

	const [data, setData] = useState(() => initialCache?.products || []);
	const [years, setYears] = useState(() => initialCache?.years || [DEFAULT_YEAR]);
	const [loading, setLoading] = useState(() => !(initialCache?.products?.length > 0));
	const [loadError, setLoadError] = useState("");
	const [reloadKey, setReloadKey] = useState(0);

	const retryProducts = useCallback(() => {
		setReloadKey((value) => value + 1);
	}, []);

	useEffect(() => {
		let active = true;
		const controller = new AbortController();
		const timeoutId = window.setTimeout(() => {
			controller.abort();
		}, REQUEST_TIMEOUT_MS);

		const loadHomeProducts = async () => {
			if (!initialCache?.products?.length) {
				setLoading(true);
			}
			setLoadError("");

			try {
				// 1) Get the available gown years from the much smaller navbar API.
				let availableYears = initialCache?.years || [DEFAULT_YEAR];

				try {
					const navbarResult = await fetchJson(
						NAVBAR_URL,
						{
							method: "POST",
							headers: {
								"Content-Type": "application/json",
							},
							body: JSON.stringify({ brand: "Risky" }),
						},
						controller.signal
					);

					const apiYears = navbarResult?.data?.Gowns?.years;
					if (Array.isArray(apiYears) && apiYears.length > 0) {
						availableYears = apiYears;
					}
				} catch (navbarError) {
					if (controller.signal.aborted) {
						throw navbarError;
					}
					// If navbar metadata fails, continue with the default/cached year.
				}

				const latestYear = getLatestYear(availableYears);

				// 2) IMPORTANT: only request one paginated chunk for the homepage.
				// This replaces /api/products/brand, which can return the whole catalog.
				const params = new URLSearchParams({
					brand: "Risky",
					category: "Gowns",
					year: String(latestYear),
					page: "1",
					limit: String(HOME_PRODUCTS_LIMIT),
				});

				const productsResult = await fetchJson(
					`${PRODUCTS_URL}?${params.toString()}`,
					{},
					controller.signal
				);

				const products = productsResult?.data?.products;

				if (!Array.isArray(products)) {
					throw new Error("The products API returned an invalid response.");
				}

				if (!active) {
					return;
				}

				setData(products);
				setYears(availableYears);
				saveHomeCache(products, availableYears);
			} catch (error) {
				if (!active) {
					return;
				}

				if (controller.signal.aborted) {
					setLoadError(
						"The product request is taking too long. Please try again."
					);
				} else {
					setLoadError(
						error?.message || "Unable to load the latest products."
					);
				}
			} finally {
				window.clearTimeout(timeoutId);
				if (active) {
					setLoading(false);
				}
			}
		};

		loadHomeProducts();

		return () => {
			active = false;
			window.clearTimeout(timeoutId);
			controller.abort();
		};
	}, [initialCache, reloadKey]);

	const latestYear = getLatestYear(years);

	return (
		<div className="relative overflow-hidden">
			<Navbar />

			<main id="main-content">
				<NewArrivalSection latestYear={latestYear} />
				<HeroSection latestYear={latestYear} />

				<div className="below-fold-content">
					<ExploreCollection
						data={data}
						loading={loading}
						error={loadError}
						onRetry={retryProducts}
					/>
					<Collections data={data} latestYear={latestYear} />
					<Features data={data} latestYear={latestYear} />
					<Shapingd />
				</div>
			</main>

			<Footer />
		</div>
	);
}

export default memo(Home);
